import { useCallback, useEffect, useState } from 'react'
import { Button, Select, useBestaetigung } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'
import { AlertTriangle, Plus, Trash2, GitMerge, X } from 'lucide-react'
import { akkuAnzeige } from '../utils/akku'

// Banner über der Akku-Liste: Seriennummern, die laut DJI geflogen sind, aber nicht in der Akku-Verwaltung stehen.
// Die Flugdaten bleiben gespeichert, bis der Akku angelegt, mit einem vorhandenen Akku zusammengeführt (Seriennummer
// fehlt/falsch – die DJI-Nummer wird am Akku hinterlegt) oder hier verworfen wird.

export interface UnbekannterAkku {
  seriennummer: string
  fluege: number
  erstmals: string | null
  zuletzt: string | null
  ladezyklen: number | null
  firmware: string | null
  dji_typ: string | null
  drohnen: string | null
}

const datum = (s: string | null) => (s ? new Date(s).toLocaleDateString('de-DE') : '—')

export function UnbekannteAkkus({ onAnlegen, aktualisieren, akkus, onZusammengefuehrt }: {
  onAnlegen: (a: UnbekannterAkku) => void
  /** Vorhandene Akkus als Ziel für das Zusammenführen */
  akkus: Array<{ id: number; bezeichnung: string; seriennummer: string | null; dji_seriennummer?: string | null; besitzer_label?: string | null }>
  /** Nach dem Zusammenführen: Akku-Liste neu laden */
  onZusammengefuehrt: () => void
  /** Ändert sich der Wert, wird neu geladen (z. B. nach dem Anlegen eines Akkus). */
  aktualisieren: number
}) {
  const { bestaetigen, dialog } = useBestaetigung()
  const [liste, setListe] = useState<UnbekannterAkku[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  /** Seriennummer, für die gerade das Zusammenführen geöffnet ist, und gewählter Ziel-Akku */
  const [mergeSn, setMergeSn] = useState<string | null>(null)
  const [ziel, setZiel] = useState('')
  const [laeuft, setLaeuft] = useState(false)

  const laden = useCallback(() => {
    api.get('/akkus/unbekannt')
      .then(r => setListe(((r.data as any).items ?? []) as UnbekannterAkku[]))
      .catch(() => setListe([]))
  }, [])
  useEffect(() => { laden() }, [laden, aktualisieren])

  async function verwerfen(a: UnbekannterAkku) {
    if (!(await bestaetigen({
      titel: 'Akku-Flugdaten verwerfen?', text: 'Unbekannter Akku', objekt: a.seriennummer,
      hinweis: `Die Akku-Werte (Spannung, Verbrauch, Zyklen) aus ${a.fluege} Flug/Flügen werden gelöscht. Die Flüge selbst bleiben im Flugbuch. Das kann nicht rückgängig gemacht werden.`,
      bestaetigenText: 'Verwerfen',
    }))) return
    setFehler(null)
    try {
      await api.delete(`/akkus/unbekannt/${encodeURIComponent(a.seriennummer)}`)
      laden()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Verwerfen fehlgeschlagen')
    }
  }

  async function zusammenfuehren(a: UnbekannterAkku) {
    const akku = akkus.find(x => String(x.id) === ziel)
    if (!akku) return
    if (!(await bestaetigen({
      titel: 'Flugdaten zuordnen?', gefahr: false, bestaetigenText: 'Zuordnen',
      text: `${a.fluege} Flug/Flüge des unbekannten Akkus ${a.seriennummer} dem Akku`, objekt: akku.bezeichnung,
      hinweis: `Die DJI-Seriennummer ${a.seriennummer} wird am Akku hinterlegt, damit künftige Flüge automatisch zugeordnet werden. Die eingetragene Seriennummer${akku.seriennummer ? ` (${akku.seriennummer})` : ''} bleibt erhalten.`,
    }))) return
    setLaeuft(true); setFehler(null)
    try {
      await api.post(`/akkus/unbekannt/${encodeURIComponent(a.seriennummer)}/zusammenfuehren`, { akku_id: akku.id })
      setMergeSn(null); setZiel('')
      laden(); onZusammengefuehrt()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Zusammenführen fehlgeschlagen')
    } finally { setLaeuft(false) }
  }

  if (!liste.length) return null
  return (
    <div className="space-y-2">
      {dialog}
      {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-2 text-sm text-red-200">{fehler}</div>}
      {liste.map(a => (
        <div key={a.seriennummer} role="status" className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-300" aria-hidden />
          <div className="min-w-0 flex-1 text-sm">
            <div className="font-semibold text-dark-text">
              Unbekannter Akku geflogen: <span className="font-mono">{a.seriennummer}</span>
            </div>
            <div className="text-xs text-dark-text-muted">
              {a.fluege} Flug/Flüge · {datum(a.erstmals)} – {datum(a.zuletzt)}
              {a.drohnen && <> · Drohne {a.drohnen}</>}
              {a.ladezyklen != null && <> · {a.ladezyklen} Ladezyklen</>}
              {a.firmware && <> · Firmware {a.firmware}</>}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-3 sm:gap-2">
            <Button size="sm" onClick={() => onAnlegen(a)}><Plus className="mr-1 h-4 w-4" />Akku anlegen</Button>
            <Button size="sm" variant="secondary" aria-expanded={mergeSn === a.seriennummer}
              onClick={() => { setMergeSn(mergeSn === a.seriennummer ? null : a.seriennummer); setZiel(''); setFehler(null) }}>
              <GitMerge className="mr-1 h-4 w-4" />Zusammenführen
            </Button>
            <button type="button" onClick={() => verwerfen(a)} title="Daten verwerfen" aria-label={`Daten von ${a.seriennummer} verwerfen`}
              className="flex h-12 w-12 items-center justify-center rounded-lg text-red-400 md:h-9 md:w-9 transition-colors hover:bg-red-400/10 hover:text-red-300">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        {mergeSn === a.seriennummer && (
          <div className="mt-3 flex flex-col gap-2 border-t border-amber-500/30 pt-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <label className="label">Gleicher physischer Akku wie …</label>
              <Select value={ziel} onChange={e => setZiel(e.target.value)} placeholder="— vorhandenen Akku wählen —"
                options={akkus.filter(x => !x.dji_seriennummer).map(x => ({
                  value: String(x.id), label: `${akkuAnzeige(x)}${x.seriennummer ? ` · SN ${x.seriennummer}` : ' · ohne Seriennummer'}`,
                }))} />
              <p className="mt-1 text-xs text-dark-text-muted">
                Für Akkus, deren Seriennummer fehlt oder falsch eingetragen ist. Akkus, denen schon eine DJI-Seriennummer
                zugeordnet ist, stehen nicht zur Auswahl.
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" disabled={!ziel || laeuft} onClick={() => zusammenfuehren(a)}>
                <GitMerge className="mr-1 h-4 w-4" />{laeuft ? 'Führe zusammen …' : 'Zusammenführen'}
              </Button>
              <button type="button" onClick={() => setMergeSn(null)} aria-label="Abbrechen" title="Abbrechen"
                className="flex h-12 w-12 items-center justify-center rounded-lg text-dark-text-muted md:h-9 md:w-9 hover:bg-dark-bg-tertiary">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
        </div>
      ))}
    </div>
  )
}
